import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api/axios.config';
import { notify } from '../utils/methods/notification';

const initialState = {
    allEvents: [],
    feasibilityPayments: {},
    isLoading: false,
    /** Slot ids for which a CR was just raised; merged into list until API returns change_requests */
    pendingCrSlotIds: {},
    payment_has_na_cycle: null,
};
// @info commenting due is defined but never used
// const getCycleNumber = (cycle) => {
//     const match = cycle?.toString().match(/\d+/);
//     return match ? Number(match[0]) : 0;
// };

// const getHighestCycle = (currentCycle, nextCycle) => {
//     if (!nextCycle) return currentCycle;
//     return getCycleNumber(nextCycle) > getCycleNumber(currentCycle) ? nextCycle : currentCycle;
// };

// fetch the all primary diagnosis list 
export const addSlotRequest = createAsyncThunk(
    'bmr/addSlotRequest',
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post('/slot-module/slot/request', data);
            notify.success(response.data.message)
            return response.data;
        } catch (error) {
            notify.error(error || error?.message || error?.data?.message)
            return rejectWithValue(error?.response ? error?.response?.data : error?.message);
        }
    }
);

// fetch the all primary diagnosis list 
export const fetchSlotList = createAsyncThunk(
    'bmr/fetchSlotList',
    async (params, { rejectWithValue }) => {
        try {
            const response = await api.get('/slot-module/slot/list', { params: params });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// fetch the all primary diagnosis list 
export const fetchSlotDetails = createAsyncThunk(
    'bmr/fetchSlotDetails',
    async (subjectId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/slot-module/slot/slot-details/${subjectId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// auto aprroved slot 
export const autoApprovedSlot = createAsyncThunk(
    'bmr/autoApprovedSlot',
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post('/slot-module/slot/slot-auto-approved', data);
            notify.success(response.data.message)
            return response.data;
        } catch (error) {
            notify.error(error || error?.message || error?.data?.message)
            return rejectWithValue(error?.response ? error?.response?.data : error?.message);
        }
    }
);

// fetch the all primary diagnosis list 
export const bookSlot = createAsyncThunk(
    'bmr/bookSlot',
    async (args, { rejectWithValue }) => {
        try {
            const { id, data } = args
            const response = await api.patch(`/slot-module/confirm/${id}/slot`, data);
            notify.success(response.data.message || response.data.data)
            return response.data;
        } catch (error) {
            notify.error(error || error?.message || error?.data?.message)
            return rejectWithValue(error?.response ? error?.response?.data : error?.message);
        }
    }
);

// fetch the all primary diagnosis list 
export const deleteSlot = createAsyncThunk(
    'bmr/deleteSlot',
    async (args, { rejectWithValue }) => {
        try {
            const { id, data } = args
            const response = await api.patch(`/slot-module/decline/${id}/slot`, data);
            notify.success(response.data.message || response.data.data)
            return response.data;
        } catch (error) {
            notify.error(error || error?.message || error?.data?.message)
            return rejectWithValue(error?.response ? error?.response?.data : error?.message);
        }
    }
);


// updateSlot 
export const updateSlot = createAsyncThunk(
    'bmr/updateSlot',
    async (args, { rejectWithValue }) => {
        try {
            const { id, data } = args
            const response = await api.patch(`/slot-module/update/${id}/slot-requests`, data);
            notify.success(response.data.message || response.data.data)
            return response.data;
        } catch (error) {
            notify.error(error || error?.message || error?.data?.message)
            return rejectWithValue(error?.response ? error?.response?.data : error?.message);
        }
    }
);


export const feasibilityPaymentStatus = createAsyncThunk(
    'bmr/feasibilityPaymentStatus',
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post('/logistics/payment-status/list', data);
            // notify.success(response.data.message);
            return response.data;
        } catch (error) {
            notify.error(error ?? error.response.message);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);




const bmrApheresisSlice = createSlice({
    name: 'bmrApheresis',
    initialState,
    reducers: {
        markPendingCrForSlot: (state, action) => {
            if (!state.pendingCrSlotIds) {
                state.pendingCrSlotIds = {};
            }
            const id = action.payload;
            if (id != null && id !== '') {
                state.pendingCrSlotIds[String(id)] = true;
            }
        },
    },
    extraReducers: (builder) => {
        /// handle add slot request
        builder
            .addCase(addSlotRequest.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(addSlotRequest.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(addSlotRequest.rejected, (state) => {
                state.isLoading = false;
            });

        builder
            .addCase(feasibilityPaymentStatus.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(feasibilityPaymentStatus.fulfilled, (state, action) => {
                state.feasibilityPayments = action.payload;
               state.payment_has_na_cycle = action.payload?.data?.has_na_cycle ?? null;
                state.highest_cycle = action.payload?.data?.highest_cycle ?? null;
                state.isLoading = false;
            })
            .addCase(feasibilityPaymentStatus.rejected, (state) => {
                state.isLoading = false;
            });


        /// handle fetch all slot request
        builder
            .addCase(fetchSlotList.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchSlotList.fulfilled, (state, action) => {
                state.isLoading = false;
                if (!state.pendingCrSlotIds) {
                    state.pendingCrSlotIds = {};
                }
                const pending = state.pendingCrSlotIds;
                const incoming = action.payload.data || [];
                state.allEvents = incoming.map((slot) => {
                    const id = String(slot?.id ?? '');
                    const hasRequests =
                        Array.isArray(slot?.change_requests) &&
                        slot.change_requests.length > 0;
                    if (hasRequests && pending[id]) {
                        delete pending[id];
                    }
                    if (pending[id] && !hasRequests) {
                        return {
                            ...slot,
                            has_open_change_request: true,
                            change_requests: [{ status: 'pending' }],
                        };
                    }
                    return slot;
                });
            })
            .addCase(fetchSlotList.rejected, (state) => {
                state.isLoading = false;
            });
        /// handle bookSlot slot request
        builder
            .addCase(bookSlot.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(bookSlot.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(bookSlot.rejected, (state) => {
                state.isLoading = false;
            });
        /// handle deleteSlot  slot request
        builder
            .addCase(deleteSlot.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteSlot.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(deleteSlot.rejected, (state) => {
                state.isLoading = false;
            });
        /// handle updateSlot  slot request
        builder
            .addCase(updateSlot.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateSlot.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(updateSlot.rejected, (state) => {
                state.isLoading = false;
            });
        /// handle fetchSlotDetails  slot request
        builder
            .addCase(fetchSlotDetails.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchSlotDetails.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchSlotDetails.rejected, (state) => {
                state.isLoading = false;
            });
        /// handle autoApprovedSlot  slot request
        builder
            .addCase(autoApprovedSlot.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(autoApprovedSlot.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(autoApprovedSlot.rejected, (state) => {
                state.isLoading = false;
            });
    },
});
export const { markPendingCrForSlot } = bmrApheresisSlice.actions;

export const selectApheresisLoading = (state) => state.bmrApheresis.isLoading;
export const selectApheresEvents = (state) => state.bmrApheresis.allEvents;
export const selectFeasibilityPayments = (state) => state.bmrApheresis.feasibilityPayments;
export const selectHighestCycle = (state) => state.bmrApheresis.highest_cycle;
export const selectPaymenthasNaCycle = (state) => state.bmrApheresis.payment_has_na_cycle;





export default bmrApheresisSlice.reducer;
